var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import '../elements/vitesse-icon';
import '../elements/vitesse-button';
/**
 * THE CHAPTERS OF A THREAD — a contents page for a conversation.
 *
 * A long thread is a document, and the one thing a document has that a chat does not is a way to get
 * back to the part you remember. Chapters exist server-side already (a person pins one, or the
 * segmenter finds them); nothing surfaced them, so they were a record nobody could read.
 *
 * A BUTTON THAT IS ABSENT WHEN THERE ARE NONE. A short thread has no structure to find, and a
 * contents button on a four-message conversation is a control that opens to say "nothing here". It
 * appears when there is something to navigate.
 *
 * HIERARCHY BY POSITION, not by a field. Chapters carry a start position and nothing else — so a
 * chapter that begins inside another one's span is drawn as its child. That keeps the data model
 * flat (a chapter is a point, which is all a pin can honestly be) while the list still reads as the
 * nested thing people expect from a contents page.
 */
let VitesseAiChapters = class VitesseAiChapters extends LitElement {
    constructor() {
        super(...arguments);
        this.chapters = [];
        this.open = false;
        /*
         * CLICKING ANYWHERE ELSE CLOSES IT.
         *
         * On the document, in the CAPTURE phase, so it fires before whatever was clicked can act — and
         * composed-path rather than `event.target`, because a click inside this element's shadow root
         * reports the host from outside and would look like an outside click. Bound only while the menu
         * is open: a permanent document listener for a menu nobody opened is a listener that runs on
         * every click in the app forever.
         */
        this.onDocClick = (e) => {
            if (e.composedPath().includes(this))
                return;
            this.open = false;
        };
        this.onKeyDown = (e) => {
            if (e.key === 'Escape')
                this.open = false;
        };
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
    }
    updated() {
        if (this.open) {
            document.addEventListener('click', this.onDocClick, true);
            document.addEventListener('keydown', this.onKeyDown);
        }
        else {
            document.removeEventListener('click', this.onDocClick, true);
            document.removeEventListener('keydown', this.onKeyDown);
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this.onDocClick, true);
        document.removeEventListener('keydown', this.onKeyDown);
    }
    static { this.styles = css `
    :host {
      position: relative;
      display: inline-flex;
    }
    .menu {
      position: absolute;
      top: calc(100% + 4px);
      right: 0;
      z-index: 30;
      width: 260px;
      /*
       * EIGHT ROWS, then it scrolls.
       *
       * A contents list is for jumping, not for reading — past about eight entries the menu stops
       * being a glance and becomes a page, and on a 360px rail a taller one covers the thread it is
       * meant to navigate. Expressed in rows rather than pixels so it survives a type-scale change:
       * row = padding (10) + line (18).
       */
      max-height: calc(8 * 28px + 8px);
      overflow-y: auto;
      padding: 4px;
      border: 1px solid var(--border-color);
      border-radius: 8px;
      background: var(--bg-primary);
      box-shadow: 0 8px 24px rgb(0 0 0 / 28%);
    }
    .row {
      display: block;
      width: 100%;
      padding: 5px 8px;
      border: 0;
      border-radius: 6px;
      background: none;
      color: var(--text-secondary);
      font: inherit;
      font-size: 12.5px;
      text-align: left;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .row:hover {
      color: var(--blue);
      background: var(--bg-secondary);
    }
    /* A nested chapter is indented, not re-styled: it is the same kind of thing one level in. */
    .row.child {
      padding-left: 20px;
    }
    /* Somebody pinned this one deliberately. Worth telling apart from what the segmenter guessed. */
    .row.mine::before {
      content: '';
      display: inline-block;
      width: 4px;
      height: 4px;
      margin-right: 6px;
      border-radius: 50%;
      background: var(--ai);
      vertical-align: middle;
    }
  `; }
    render() {
        if (!this.chapters.length)
            return nothing;
        const rows = nest(this.chapters);
        return html `
      <vitesse-button
        size="sm"
        icon="table-of-contents"
        title=${t('ai.chapters')}
        @click=${() => (this.open = !this.open)}
      ></vitesse-button>
      ${this.open
            ? html `<div class="menu" @click=${() => (this.open = false)}>
              ${rows.map((r) => html `<button
                    class="row ${r.depth > 0 ? 'child' : ''} ${r.chapter.source === 'user' ? 'mine' : ''}"
                    title=${r.chapter.title}
                    @click=${() => this.onJump?.(r.chapter.startSeq)}
                  >
                    ${r.chapter.title}
                  </button>`)}
            </div>`
            : nothing}
    `;
    }
};
__decorate([
    property({ attribute: false })
], VitesseAiChapters.prototype, "chapters", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiChapters.prototype, "onJump", void 0);
__decorate([
    state()
], VitesseAiChapters.prototype, "open", void 0);
VitesseAiChapters = __decorate([
    customElement('vitesse-ai-chapters')
], VitesseAiChapters);
export { VitesseAiChapters };
/**
 * Depth from position alone.
 *
 * Chapters are points in a thread, so there is no parent field to read — but the segmenter emits
 * broad chapters and finer ones inside them, and drawing them flat loses exactly the structure a
 * contents page is for. A chapter nests under the last one that started before it AND is separated
 * by more than one message: two chapters pinned at adjacent messages are siblings, not a hierarchy,
 * which is the case a naive "starts after" rule gets wrong.
 */
export function nest(chapters) {
    const sorted = [...chapters].sort((a, b) => a.startSeq - b.startSeq);
    const out = [];
    const stack = [];
    for (const c of sorted) {
        while (stack.length && c.startSeq - stack[stack.length - 1].startSeq <= 1)
            stack.pop();
        out.push({ chapter: c, depth: Math.min(stack.length, 2) });
        stack.push(c);
        // Two levels is a contents page; three is an outline nobody scans.
        if (stack.length > 2)
            stack.shift();
    }
    return out;
}
//# sourceMappingURL=chapters-menu.js.map